// components
import Input from '@components/Input/Input';
// types
import { SideEditorScrollType } from '@components/SideEditor/SideEditor';
import { useSideEditorContext } from '@components/SideEditor/utils';
import { emailSignatureSideEditorServicesContext } from '@context/email-signature-services-context';
// hooks and contexts
import useNode from '@hooks/use-node';
// utils
import Emitter from '@modules/event-emitter';
import { type FC, useContext, useEffect } from 'react';

import { SideEditorNodeKey } from '../../types';
import EmailSignatureEditorPanelSection, {
  EmailSignatureEditorPanelSectionContents,
  EmailSignatureEditorPanelSectionHeading,
} from '../EmailSignatureEditorPanelSection';
import EmailSignatureLockLayer from '../EmailSignatureLockLayer';
import styles from './EmailSignatureEditorPersonalSection.module.scss';

const EmailSignatureSideEditorPersonalSection: FC<React.PropsWithChildren<unknown>> = () => {
  const [personalDetailsSectionDiv, personalDetailsSectionDivRef] = useNode<HTMLDivElement>();
  const { emailSignature, emailSignatureDispatch, isInTransitionState, isSignatureLocked } = useContext(
    emailSignatureSideEditorServicesContext,
  );
  const { personalDetails } = emailSignature;
  const { scrollIntoView, panelIndex, registerNode } = useSideEditorContext();

  useEffect(() => {
    if (!personalDetailsSectionDiv) return;
    registerNode(SideEditorNodeKey.PERSONAL_DETAILS_NODE, {
      panelBelongTo: panelIndex,
      node: personalDetailsSectionDiv,
    });
  }, [personalDetailsSectionDiv, panelIndex, registerNode]);

  useEffect(() => {
    const handleScrollToPersonalDetails = () => {
      if (isInTransitionState) {
        return;
      }
      scrollIntoView(SideEditorNodeKey.PERSONAL_DETAILS_NODE, SideEditorScrollType.START);
    };
    Emitter.on('SCROLL_TO_PERSONAL_DETAILS', handleScrollToPersonalDetails);

    return () => {
      Emitter.off('SCROLL_TO_PERSONAL_DETAILS', handleScrollToPersonalDetails);
    };
  }, [isInTransitionState, scrollIntoView]);

  return (
    <EmailSignatureEditorPanelSection ref={personalDetailsSectionDivRef}>
      <EmailSignatureEditorPanelSectionHeading heading="Personal details" />
      <EmailSignatureEditorPanelSectionContents>
        <div className={styles['personal-details-inputs-container']}>
          <div className={styles['personal-details-names-inputs-container']}>
            <div className={styles['personal-details-names-input']}>
              <PersonalDetailsField
                label={'First name'}
                isLocked={isSignatureLocked}
                value={personalDetails.firstName}
                onChange={(newValue: string) => {
                  emailSignatureDispatch({ type: 'UPDATE_FIRST_NAME', value: newValue });
                }}
              />
            </div>
            <div className={styles['personal-details-names-input']}>
              <PersonalDetailsField
                isLocked={isSignatureLocked}
                label={'Last name'}
                value={personalDetails.lastName}
                onChange={(newValue: string) => {
                  emailSignatureDispatch({ type: 'UPDATE_LAST_NAME', value: newValue });
                }}
              />
            </div>
          </div>
          <PersonalDetailsField
            label={'Job title'}
            isLocked={isSignatureLocked}
            value={personalDetails.jobTitle}
            onChange={(newValue: string) => {
              emailSignatureDispatch({ type: 'UPDATE_JOB_TITLE', value: newValue });
            }}
          />
          <PersonalDetailsField
            label={'Company'}
            isLocked={isSignatureLocked}
            value={personalDetails.company}
            onChange={(newValue: string) => {
              emailSignatureDispatch({ type: 'UPDATE_COMPANY', value: newValue });
            }}
          />
          <PersonalDetailsField
            isLocked={isSignatureLocked}
            label={'Headline'}
            value={personalDetails.headline}
            onChange={(newValue: string) => {
              emailSignatureDispatch({ type: 'UPDATE_QUOTE', value: newValue });
            }}
          />
          {isSignatureLocked && (
            <EmailSignatureLockLayer message="You cannot edit this section, please contact your admin if you have any questions." />
          )}
        </div>
      </EmailSignatureEditorPanelSectionContents>
    </EmailSignatureEditorPanelSection>
  );
};

type PersonalDetailsFieldProps = {
  label: string;
  value: string;
  onChange: (newValue: string) => void;
  isLocked: boolean;
};

const PersonalDetailsField: FC<React.PropsWithChildren<PersonalDetailsFieldProps>> = ({
  label,
  value,
  onChange,
  isLocked,
}) => {
  return (
    <Input
      disabled={isLocked}
      label={label}
      type={'text'}
      enableClearButton={!isLocked}
      value={value}
      onChange={onChange}
      contained
      hideAllErrorMessages
    />
  );
};

export default EmailSignatureSideEditorPersonalSection;
